/**
 * Home Hero Mosaic carousel.
 *
 * Purpose-built rather than reusing Dawn's slideshow: that one transitions by
 * scrolling a track, which cannot stagger layers or reverse direction
 * independently of scroll position. See docs/home-hero/03-motion-spec.md.
 */

const TRANSITION_TOTAL = 780;
const INPUT_LOCK = 400;
const DRAG_AXIS_THRESHOLD = 8;
const DRAG_COMMIT_RATIO = 0.18;
const DRAG_FLICK_VELOCITY = 0.5;
const DRAG_FLICK_MIN_DISTANCE = 40;

class HomeHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.hh-carousel__viewport');
    this.slides = Array.from(this.querySelectorAll('.hh-slide'));
    this.dots = Array.from(this.querySelectorAll('.hh-carousel__dot'));
    this.live = this.querySelector('.hh-carousel__live');

    if (this.slides.length === 0) return;

    this.index = 0;
    this.elapsed = 0;
    this.queued = null;
    this.transitionEndsAt = 0;
    this.pauseReasons = new Set();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.interval = Number(this.dataset.interval || 6000);
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion.matches;

    this.syncState();
    this.bind();

    if (this.autoplay) {
      this.lastTs = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.observer) this.observer.disconnect();
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.cleanupTimer) clearTimeout(this.cleanupTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* ----------------------------------------------------------------------
     Wiring
     ---------------------------------------------------------------------- */

  bind() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.request(i));
    });

    this.addEventListener('keydown', this.onKeydown);

    this.addEventListener('pointerenter', () => this.pause('hover'));
    this.addEventListener('pointerleave', () => this.resume('hover'));
    this.addEventListener('focusin', () => this.pause('focus'));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume('focus');
    });

    this.onVisibility = () => {
      if (document.visibilityState === 'hidden') this.pause('tab');
      else this.resume('tab');
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this.resume('offscreen');
          else this.pause('offscreen');
        },
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }

    if (this.slides.length > 1) this.bindDrag();
  }

  /* ----------------------------------------------------------------------
     Navigation
     ---------------------------------------------------------------------- */

  /** A user-initiated change. Latches autoplay off for good. */
  request(index, direction) {
    if (index === this.index) return;
    this.stopAutoplay();
    this.announce(index);
    this.goTo(index, direction);
  }

  goTo(index, direction) {
    const now = performance.now();

    // Never interrupt a transition mid-flight. One queued request maximum —
    // a later click replaces the queued index rather than stacking.
    if (now < this.transitionEndsAt) {
      if (now < this.transitionEndsAt - (TRANSITION_TOTAL - INPUT_LOCK)) return;
      this.queued = index;
      return;
    }

    const previous = this.slides[this.index];
    const next = this.slides[index];
    if (!next || next === previous) return;

    const dir = direction || (index > this.index ? 1 : -1);
    this.style.setProperty('--hh-dir', dir);

    previous.classList.remove('is-active');
    previous.classList.add('is-leaving');
    previous.setAttribute('data-leave', '');

    next.classList.add('is-active');
    next.setAttribute('data-enter', '');

    this.index = index;
    this.elapsed = 0;
    this.syncState();

    this.transitionEndsAt = now + TRANSITION_TOTAL;
    if (this.cleanupTimer) clearTimeout(this.cleanupTimer);

    this.pendingCleanup = (runQueued = true) => {
      this.pendingCleanup = null;
      this.transitionEndsAt = 0;
      previous.classList.remove('is-leaving');
      previous.removeAttribute('data-leave');
      next.removeAttribute('data-enter');

      if (runQueued && this.queued !== null) {
        const queued = this.queued;
        this.queued = null;
        this.goTo(queued);
      }
    };

    this.cleanupTimer = setTimeout(() => this.pendingCleanup && this.pendingCleanup(), TRANSITION_TOTAL);
  }

  /**
   * Land an in-flight transition right now. A finger arriving mid-animation
   * must take control immediately — a carousel that ignores the first touch
   * because it happens to be auto-advancing feels broken.
   */
  finishTransition() {
    if (this.cleanupTimer) clearTimeout(this.cleanupTimer);
    this.cleanupTimer = null;
    this.queued = null;
    if (this.pendingCleanup) this.pendingCleanup(false);
    this.transitionEndsAt = 0;
  }

  next() {
    return (this.index + 1) % this.slides.length;
  }

  previous() {
    return (this.index - 1 + this.slides.length) % this.slides.length;
  }

  /** Mirrors DOM state to the current index: active slide, aria, inert, dots. */
  syncState() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === this.index));
    });

    // Pagination and the progress bar invert on the light surfaces.
    const surface = this.slides[this.index].dataset.surface;
    const light = surface === 'mint' || surface === 'yellow' || surface === 'powder';
    this.style.setProperty('--hh-pag-color', light ? '#04125B' : '#FFFFFF');
    this.style.setProperty('--hh-progress-fill', light ? '#3550E9' : '#FFCF31');
    this.style.setProperty('--hh-progress', 0);
  }

  announce(index) {
    if (this.live) this.live.textContent = `Slide ${index + 1} of ${this.slides.length}`;
  }

  onKeydown = (event) => {
    const keys = {
      ArrowLeft: () => this.previous(),
      ArrowRight: () => this.next(),
      Home: () => 0,
      End: () => this.slides.length - 1,
    };
    const resolve = keys[event.key];
    if (!resolve) return;
    event.preventDefault();
    const target = resolve();
    this.request(target, event.key === 'ArrowLeft' || event.key === 'Home' ? -1 : 1);
  };

  /* ----------------------------------------------------------------------
     Autoplay
     ---------------------------------------------------------------------- */

  tick = (ts) => {
    this.rafId = requestAnimationFrame(this.tick);
    if (!this.autoplay) return;

    const delta = this.lastTs ? ts - this.lastTs : 0;
    this.lastTs = ts;

    // Paused: hold the progress underline where it is, don't reset it.
    if (this.pauseReasons.size > 0 || performance.now() < this.transitionEndsAt) return;

    this.elapsed += delta;
    const progress = Math.min(this.elapsed / this.interval, 1);
    this.style.setProperty('--hh-progress', progress);

    if (progress >= 1) {
      this.elapsed = 0;
      this.goTo(this.next(), 1);
    }
  };

  pause(reason) {
    this.pauseReasons.add(reason);
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    this.lastTs = 0;
  }

  /** One-way latch. Once a visitor takes control, autoplay never resumes. */
  stopAutoplay() {
    if (!this.autoplay) return;
    this.autoplay = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.style.setProperty('--hh-progress', 0);
  }

  /* ----------------------------------------------------------------------
     Drag / swipe
     ---------------------------------------------------------------------- */

  bindDrag() {
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: false });
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);

    // A drag that ends over a CTA must not also follow the link.
    this.viewport.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      },
      true
    );

    // Native image dragging fights pointer capture on desktop.
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
  }

  onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.dragging) return;

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startTime = performance.now();
    this.axis = null;
    this.dx = 0;
    this.suppressClick = false;
  };

  onPointerMove = (event) => {
    if (event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;

    // Axis lock. Until the gesture proves horizontal we touch nothing, so a
    // vertical flick scrolls the page exactly as it would over static content.
    if (!this.axis) {
      if (Math.abs(dx) < DRAG_AXIS_THRESHOLD && Math.abs(dy) < DRAG_AXIS_THRESHOLD) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        this.axis = 'y';
        this.pointerId = null;
        return;
      }
      this.axis = 'x';
      this.dragging = true;
      this.finishTransition();
      this.stopAutoplay();
      this.classList.add('is-dragging');
      this.classList.remove('is-settling');
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is an optimisation, not a requirement */
      }
    }

    if (this.axis !== 'x') return;

    event.preventDefault();
    this.dx = dx;
    if (Math.abs(dx) > DRAG_AXIS_THRESHOLD) this.suppressClick = true;
    this.applyDrag(dx);
  };

  applyDrag(dx) {
    const width = this.viewport.clientWidth || 1;
    const ratio = Math.max(-1, Math.min(1, dx / width));
    const magnitude = Math.abs(ratio);
    const neighbourIndex = dx < 0 ? this.next() : this.previous();
    const neighbour = this.slides[neighbourIndex];
    const active = this.slides[this.index];

    if (this.peek && this.peek !== neighbour) this.clearPeek();
    this.peek = neighbour;
    this.peek.classList.add('is-peek');

    active.style.transform = `translateX(${dx}px)`;
    active.style.opacity = String(1 - magnitude * 0.5);

    neighbour.style.transform = `translateX(${dx - Math.sign(dx) * width}px)`;
    neighbour.style.opacity = String(magnitude);
  }

  onPointerUp = (event) => {
    if (event.pointerId !== this.pointerId && this.axis !== 'x') {
      // Includes the axis-locked-vertical case, where pointerId was released
      // on the first move so the browser could scroll the page.
      this.axis = null;
      this.pointerId = null;
      return;
    }

    this.pointerId = null;

    if (this.axis !== 'x') {
      this.axis = null;
      return;
    }

    const width = this.viewport.clientWidth || 1;
    const dx = this.dx;
    const duration = Math.max(performance.now() - this.startTime, 1);
    const velocity = Math.abs(dx) / duration;
    // A flick counts only if it also travelled far enough to be a deliberate
    // gesture — otherwise a fast twitch on the card changes the slide.
    const commit =
      Math.abs(dx) > width * DRAG_COMMIT_RATIO ||
      (velocity > DRAG_FLICK_VELOCITY && Math.abs(dx) > DRAG_FLICK_MIN_DISTANCE);

    const active = this.slides[this.index];
    const neighbour = this.peek;
    const direction = dx < 0 ? 1 : -1;

    this.classList.remove('is-dragging');
    this.classList.add('is-settling');

    if (commit && neighbour) {
      active.style.transform = `translateX(${Math.sign(dx) * width}px)`;
      active.style.opacity = '0';
      neighbour.style.transform = 'translateX(0px)';
      neighbour.style.opacity = '1';
    } else {
      active.style.transform = 'translateX(0px)';
      active.style.opacity = '1';
      if (neighbour) {
        neighbour.style.transform = `translateX(${-Math.sign(dx || 1) * width}px)`;
        neighbour.style.opacity = '0';
      }
    }

    const settle = this.reducedMotion.matches ? 0 : 380;
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      if (commit && neighbour) {
        this.index = this.slides.indexOf(neighbour);
        this.style.setProperty('--hh-dir', direction);
        this.announce(this.index);
      }
      this.classList.remove('is-settling');
      active.style.cssText = '';
      if (neighbour) neighbour.style.cssText = '';
      this.clearPeek();
      this.syncState();
      this.dragging = false;
      this.axis = null;
      this.dx = 0;
    }, settle + 20);
  };

  clearPeek() {
    if (!this.peek) return;
    this.peek.classList.remove('is-peek');
    this.peek.style.cssText = '';
    this.peek = null;
  }
}

if (!customElements.get('home-hero-carousel')) {
  customElements.define('home-hero-carousel', HomeHeroCarousel);
}
